<template>
  <v-app
    class="booking-app"
    :style="paletteStyle"
  >
    <v-container
      fluid
      class="booking-engine pa-0"
    >
      <v-app-bar
        class="booking-app-bar"
        color="transparent"
        dark
        flat
        height="70"
      >
        <v-container class="d-flex align-center justify-space-between py-0">
          <div>
            <div class="header-title">
              {{ hotel.name }}
            </div>
            <div class="header-subtitle">
              Gerenciamento de Reserva
            </div>
          </div>
          <div
            v-if="hotel.phone || hotel.email"
            class="header-contact"
          >
            <v-chip
              v-if="hotel.phone"
              small
              outlined
              color="white"
              text-color="white"
              class="header-chip mr-2"
            >
              <v-icon
                left
                small
              >
                mdi-phone
              </v-icon>
              {{ hotel.phone }}
            </v-chip>
            <v-chip
              v-if="hotel.email"
              small
              outlined
              color="white"
              text-color="white"
              class="header-chip"
            >
              <v-icon
                left
                small
              >
                mdi-email
              </v-icon>
              {{ hotel.email }}
            </v-chip>
          </div>
        </v-container>
      </v-app-bar>

      <div class="main-content">
        <section class="hero-section">
          <span class="hero-shape hero-shape--one"></span>
          <span class="hero-shape hero-shape--two"></span>
          <v-container class="py-10 text-center">
            <p class="hero-kicker">
              Portal do Hóspede
            </p>
            <h1 class="hero-title">
              Cancelar minha reserva
            </h1>
            <p class="hero-subtitle">
              Aqui você pode confirmar o cancelamento de forma rápida e segura. Revise as informações abaixo antes de prosseguir.
            </p>
          </v-container>
        </section>

        <v-container class="engine-container pb-12">
          <div class="engine-background"></div>

          <v-progress-circular
            v-if="loading"
            indeterminate
            color="primary"
            size="64"
            width="4"
            class="d-flex mx-auto my-12"
          ></v-progress-circular>

          <v-alert
            v-else-if="errorMessage"
            type="error"
            prominent
            class="glass-card"
          >
            {{ errorMessage }}
          </v-alert>

          <template v-else>
            <v-card
              class="engine-card glass-card"
              elevation="8"
            >
              <div class="reservation-grid">
                <section class="reservation-summary">
                  <div class="section-head">
                    <div class="section-heading">
                      <p class="summary-label mb-1">
                        Reserva
                      </p>
                      <h2 class="summary-title">
                        #{{ reservation.id }} • {{ reservation.guest }}
                      </h2>
                    </div>
                    <div
                      class="status-pill"
                      :class="{ 'is-cancelled': cancelled }"
                    >
                      <v-icon small>
                        {{ cancelled ? icons.statusCancelled : icons.statusActive }}
                      </v-icon>
                      <span>{{ cancelled ? 'Reserva cancelada' : 'Reserva ativa' }}</span>
                    </div>
                  </div>
                  <p class="summary-subtitle">
                    Revise as informações da estadia e confirme o cancelamento apenas quando estiver pronto.
                  </p>

                  <div class="info-grid">
                    <div class="info-item">
                      <span class="info-label">Check-in</span>
                      <span class="info-value">{{ formatDisplayDate(reservation.check_in) }}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Check-out</span>
                      <span class="info-value">{{ formatDisplayDate(reservation.check_out) }}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Noites</span>
                      <span class="info-value">{{ reservation.nights || '-' }}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Categoria</span>
                      <span class="info-value">{{ reservation.category || '—' }}</span>
                    </div>
                  </div>

                  <div class="financial-panel">
                    <div class="financial-item">
                      <p class="info-label">
                        Valor total
                      </p>
                      <p class="financial-value">
                        {{ formatCurrency(financial.total) }}
                      </p>
                    </div>
                    <div class="financial-item">
                      <p class="info-label">
                        Multa de cancelamento
                      </p>
                      <p class="financial-value text-error">
                        {{ formatCurrency(financial.fee) }}
                      </p>
                    </div>
                    <div class="financial-item highlight">
                      <p class="info-label">
                        Reembolso estimado
                      </p>
                      <p class="financial-value">
                        {{ formatCurrency(financial.refundable) }}
                      </p>
                    </div>
                  </div>
                </section>

                <section
                  class="action-panel"
                  :class="{ 'is-cancelled': cancelled }"
                >
                  <div class="action-panel__icon">
                    <v-icon
                      large
                      :color="cancelled ? '#0f6050' : '#7d2944'"
                    >
                      {{ cancelled ? icons.actionCancelled : icons.actionActive }}
                    </v-icon>
                  </div>
                  <h3 class="action-panel__title">
                    {{ cancelled ? 'Reserva já cancelada' : 'Pronto para cancelar?' }}
                  </h3>
                  <p class="action-panel__subtitle">
                    {{
                      cancelled
                        ? 'Esta reserva foi cancelada e os valores foram atualizados de acordo com a política.'
                        : 'Ao confirmar, o sistema cancela imediatamente a reserva e aplica a multa prevista.'
                    }}
                  </p>

                  <div
                    v-if="!cancelled"
                    class="action-checklist"
                  >
                    <div class="action-checklist__item">
                      <v-icon small>
                        mdi-shield-check
                      </v-icon>
                      Política de cancelamento aplicada automaticamente.
                    </div>
                    <div class="action-checklist__item">
                      <v-icon small>
                        mdi-cash-refund
                      </v-icon>
                      Reembolso estimado exibido abaixo.
                    </div>
                    <div class="action-checklist__item">
                      <v-icon small>
                        mdi-email-check
                      </v-icon>
                      Você receberá um e-mail confirmando a operação.
                    </div>
                  </div>
                  <div
                    v-else
                    class="action-checklist action-checklist--muted"
                  >
                    <div class="action-checklist__item">
                      <v-icon small>
                        mdi-history
                      </v-icon>
                      Caso precise reativar a reserva, fale com a recepção.
                    </div>
                  </div>

                  <v-btn
                    large
                    block
                    class="action-panel__btn"
                    :class="cancelled ? 'action-panel__btn--cancelled' : 'action-panel__btn--primary'"
                    :loading="submitting"
                    :disabled="cancelled"
                    @click="confirmCancellation"
                  >
                    {{ cancelled ? 'Reserva cancelada' : 'Confirmar cancelamento' }}
                  </v-btn>
                  <p class="action-panel__help">
                    Precisa de ajuda?
                    <span v-if="hotel.phone">Ligue {{ hotel.phone }}</span>
                    <template v-if="hotel.phone && hotel.email">
                      ou
                    </template>
                    <span v-if="hotel.email">escreva para {{ hotel.email }}</span>.
                  </p>
                </section>
              </div>
            </v-card>

            <v-card
              v-if="policyRules.length"
              class="policy-card glass-card mt-6"
              elevation="0"
            >
              <v-card-title class="policy-card__title">
                <v-icon
                  left
                  color="primary"
                >
                  mdi-shield-check
                </v-icon>
                Política de Cancelamento
              </v-card-title>
              <v-card-text>
                <p
                  v-if="policyFreeDaysText"
                  class="text--secondary mb-5"
                >
                  {{ policyFreeDaysText }}
                </p>
                <v-row dense>
                  <v-col
                    v-for="(rule, index) in policyRules"
                    :key="`policy-${index}`"
                    cols="12"
                    md="4"
                  >
                    <div class="policy-rule">
                      <div class="policy-rule__title">
                        {{ rule.title }}
                      </div>
                      <div class="policy-rule__description">
                        {{ rule.description }}
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </template>
        </v-container>
      </div>
    </v-container>
  </v-app>
</template>

<script>
import themeConfig from '@themeConfig'
import {
  mdiAlertDecagram,
  mdiCalendarCheckOutline,
  mdiCheckCircleOutline,
  mdiCheckDecagram,
} from '@mdi/js'

export default {
  name: 'CancelReservation',
  data() {
    return {
      token: this.$route.params.token,
      loading: true,
      submitting: false,
      cancelled: false,
      errorMessage: null,
      reservation: {
        id: '—',
        guest: '—',
        check_in: null,
        check_out: null,
        nights: null,
        category: null,
        status: null,
      },
      financial: {
        total: 0,
        fee: 0,
        refundable: 0,
      },
      hotel: {
        name: 'Chess Hotel',
        phone: null,
        email: null,
        site: null,
      },
      policy: null,
      icons: {
        statusActive: mdiCalendarCheckOutline,
        statusCancelled: mdiCheckCircleOutline,
        actionActive: mdiAlertDecagram,
        actionCancelled: mdiCheckDecagram,
      },
      bookingColors: {
        heroStart: '#0d47a1',
        heroEnd: '#1e88e5',
        backgroundTop: '#e3f2fd',
        backgroundMid: '#f7f9fc',
        backgroundBottom: '#ffffff',
        cardStart: '#ffffff',
        cardEnd: '#e8f1ff',
        textPrimary: '#1f2a37',
        textSecondary: '#5d6473',
        outline: '#dce7f6',
      },
    }
  },
  computed: {
    paletteStyle() {
      const colors = {
        ...this.bookingColors,
        ...(themeConfig.bookingEngine || {}),
      }

      return {
        '--hero-start': colors.heroStart,
        '--hero-end': colors.heroEnd,
        '--bg-top': colors.backgroundTop,
        '--bg-mid': colors.backgroundMid,
        '--bg-bottom': colors.backgroundBottom,
        '--card-start': colors.cardStart,
        '--card-end': colors.cardEnd,
        '--text-primary': colors.textPrimary,
        '--text-muted': colors.textSecondary,
        '--outline-color': colors.outline,
      }
    },
    policyRules() {
      if (!this.policy || !Array.isArray(this.policy.rules)) {
        return []
      }

      return this.policy.rules
    },
    policyFreeDaysText() {
      if (!this.policy) {
        return ''
      }
      const days = this.policy.free_days || 0
      if (days <= 0) {
        return ''
      }

      return `Cancelamento gratuito até ${days} ${days === 1 ? 'dia' : 'dias'} antes do check-in.`
    },
  },
  mounted() {
    this.fetchDetails()
  },
  methods: {
    pushDataLayer(event, payload = {}) {
      if (!window.dataLayer || !Array.isArray(window.dataLayer)) {
        return
      }
      window.dataLayer.push({ event, ...payload })
    },
    async fetchDetails() {
      this.loading = true
      this.errorMessage = null
      try {
        const { data } = await this.$http.get(`public/booking-engine/cancel/${this.token}`)
        if (data.success) {
          this.reservation = data.data.reservation
          this.financial = data.data.financial
          this.policy = data.data.policy
          this.hotel = { ...this.hotel, ...(data.data.hotel || {}) }
          this.cancelled = this.reservation.status === 'canceled'
        } else {
          this.errorMessage = data.message || 'Não foi possível carregar a reserva.'
        }
      } catch (error) {
        const message = error.response?.data?.message || 'Link inválido ou expirado.'
        this.errorMessage = message
      } finally {
        this.loading = false
      }
    },
    async confirmCancellation() {
      const confirmation = await this.$swal.fire({
        icon: 'warning',
        title: 'Confirmar cancelamento',
        text: 'Deseja realmente cancelar esta reserva?',
        showCancelButton: true,
        confirmButtonText: 'Sim, cancelar',
        cancelButtonText: 'Manter reserva',
      })

      if (!confirmation.isConfirmed) {
        return
      }

      this.submitting = true
      try {
        const { data } = await this.$http.post(`public/booking-engine/cancel/${this.token}`)
        if (data.success) {
          this.cancelled = true
          this.reservation.status = 'canceled'
          this.financial.fee = data.data.fee
          this.financial.refundable = data.data.refundable
          this.pushDataLayer('cancel_reservation', {
            transaction_id: String(this.reservation.id || ''),
            fee: Number(data.data.fee || 0),
            refundable: Number(data.data.refundable || 0),
          })
          this.$swal.fire('Pronto!', 'Reserva cancelada com sucesso.', 'success')
        } else {
          this.$swal.fire('Ops', data.message || 'Não foi possível cancelar.', 'error')
        }
      } catch (error) {
        const message = error.response?.data?.message || 'Não foi possível cancelar a reserva.'
        this.$swal.fire('Ops', message, 'error')
      } finally {
        this.submitting = false
      }
    },
    formatCurrency(value) {
      const number = Number(value || 0)

      return number.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      })
    },
    formatDisplayDate(value) {
      if (!value) return '--/--/----'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) {
        return value
      }

      return date.toLocaleDateString('pt-BR', { timeZone: 'UTC' })
    },
  },
}
</script>

<style scoped>
.booking-app {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.booking-engine {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: linear-gradient(180deg, var(--bg-top), var(--bg-mid) 50%, var(--bg-bottom));
}

.booking-app-bar {
  background: linear-gradient(90deg, var(--hero-start), var(--hero-end)) !important;
  box-shadow: 0 8px 20px rgba(13, 71, 161, 0.35);
}

.header-title {
  font-size: 1.1rem;
  font-weight: 700;
}

.header-subtitle {
  font-size: 0.85rem;
  opacity: 0.85;
  letter-spacing: 0.05em;
}

.header-contact .header-chip {
  border-color: rgba(255, 255, 255, 0.6) !important;
  font-weight: 600;
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.hero-section {
  background: linear-gradient(120deg, var(--hero-start), var(--hero-end));
  color: #fff;
  position: relative;
  overflow: hidden;
}

.hero-shape {
  position: absolute;
  border-radius: 50%;
  opacity: 0.25;
}

.hero-shape--one {
  width: 220px;
  height: 220px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.55), transparent 70%);
  top: -60px;
  left: -40px;
}

.hero-shape--two {
  width: 320px;
  height: 320px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.3), transparent 60%);
  bottom: -90px;
  right: -60px;
}

.hero-kicker {
  letter-spacing: 0.2em;
  text-transform: uppercase;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.8);
}

.hero-title {
  font-size: 2.3rem;
  margin-bottom: 12px;
}

.hero-subtitle {
  font-size: 1rem;
  max-width: 560px;
  margin: 0 auto;
  color: rgba(255, 255, 255, 0.9);
}

.engine-container {
  margin-top: -40px;
  position: relative;
  z-index: 2;
  padding-left: 16px;
  padding-right: 16px;
}

.engine-background {
  position: absolute;
  inset: 0;
  margin: 0 16px;
  border-radius: 32px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.94), rgba(227, 242, 253, 0.92));
  box-shadow: 0 35px 70px rgba(13, 71, 161, 0.15);
}

.glass-card {
  backdrop-filter: blur(8px);
  background: rgba(255, 255, 255, 0.96);
}

.engine-card {
  border-radius: 28px;
  box-shadow: 0 25px 60px rgba(15, 76, 129, 0.25);
}

.summary-label {
  font-size: 0.8rem;
  text-transform: uppercase;
  color: var(--text-muted);
}

.summary-value {
  font-weight: 600;
  color: var(--text-primary);
  font-size: 1rem;
}

.summary-title {
  font-size: 1.25rem;
  font-weight: 700;
}

.summary-subtitle {
  color: var(--text-muted);
  margin-bottom: 24px;
}

.reservation-grid {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.reservation-summary {
  padding: 32px;
}

@media (min-width: 960px) {
  .reservation-grid {
    flex-direction: row;
  }
  .reservation-summary {
    flex: 7;
  }
  .action-panel {
    flex: 5;
  }
}

@media (max-width: 600px) {
  .reservation-summary {
    padding: 24px;
  }
}

.section-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.section-heading {
  flex: 1;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.9rem;
  background: rgba(30, 136, 229, 0.12);
  color: #1d3b64;
}

.status-pill.is-cancelled {
  background: rgba(76, 175, 80, 0.15);
  color: #1f5138;
}

.status-pill v-icon {
  color: currentColor !important;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 16px;
}

.info-item {
  padding: 12px 14px;
  border: 1px solid rgba(30, 136, 229, 0.08);
  border-radius: 12px;
  background: rgba(236, 244, 255, 0.35);
  min-height: 84px;
}

.info-label {
  display: block;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
  margin-bottom: 6px;
}

.info-value {
  display: block;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-primary);
}

.financial-panel {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-top: 24px;
}

.financial-item {
  padding: 16px;
  border-radius: 16px;
  border: 1px solid rgba(12, 54, 103, 0.08);
  background: white;
  box-shadow: 0 10px 30px rgba(15, 76, 129, 0.08);
}

.financial-item.highlight {
  background: linear-gradient(135deg, rgba(25, 118, 210, 0.1), rgba(25, 118, 210, 0.02));
  border-color: rgba(25, 118, 210, 0.2);
}

.financial-value {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--text-primary);
}

.summary-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
}

.action-panel {
  background: linear-gradient(135deg, #fdf2f6, #f8e4ed);
  border-radius: 24px;
  padding: 32px 28px;
  color: #7d2944;
  box-shadow: 0 30px 45px rgba(244, 104, 168, 0.12);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.action-panel.is-cancelled {
  background: linear-gradient(135deg, #ecfbf6, #d8f4ff);
  box-shadow: 0 30px 45px rgba(33, 150, 243, 0.15);
  color: #0d5a48;
}

.action-panel__icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.action-panel__title {
  font-size: 1.4rem;
  font-weight: 700;
  margin-bottom: 8px;
}

.action-panel__subtitle {
  opacity: 0.85;
  line-height: 1.4;
  margin-bottom: 16px;
}

.action-checklist {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
}

.action-checklist__item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
}

.action-checklist--muted {
  opacity: 0.9;
}

.action-panel__btn {
  font-weight: 700;
  margin-bottom: 12px;
}

.action-panel__btn--primary {
  background: linear-gradient(90deg, #f78ca0, #f9748f) !important;
  color: #fff !important;
  box-shadow: none;
}

.action-panel__btn--cancelled {
  background: linear-gradient(90deg, #5ed4b7, #64c9f3) !important;
  color: #fff !important;
  box-shadow: none;
}

.action-panel__btn:disabled {
  opacity: 0.75 !important;
}

.action-panel__help {
  font-size: 0.9rem;
  opacity: 0.85;
  margin-bottom: 0;
  color: currentColor;
}

.policy-card {
  border-radius: 22px;
  border: 1px solid rgba(25, 118, 210, 0.1);
}

.policy-card__title {
  font-weight: 700;
  color: var(--text-primary);
}

.policy-rule {
  background: rgba(25, 118, 210, 0.05);
  border-radius: 16px;
  padding: 16px;
  min-height: 120px;
  border: 1px solid rgba(25, 118, 210, 0.08);
}

.policy-rule__title {
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 4px;
}

.policy-rule__description {
  color: var(--text-muted);
  font-size: 0.95rem;
  line-height: 1.4;
}
</style>
